import React, { useState } from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';
import Modal from '@/components/layout/Modal';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import AlertBox from '@/components/layout/AlertBox';
import CodeBlock from '@/components/layout/CodeBlock';
import Botao from '@/components/Botao';
import Icon from '@mdi/react';
import { mdiLoading } from '@mdi/js';
import { getInstallBaseUrl } from '@/lib/tenant';
import type { AgenteDetalheDTO } from '@webip/shared/src/dto/agente';
import type { NetworkLanDeviceDTO } from '@webip/shared/src/dto/appliance';

type Platform = 'linux' | 'windows';

const PlatformToggle = ({ platform, onChange }: { platform: Platform; onChange: (p: Platform) => void }) => (
    <HBox style={{ gap: '8px' }}>
        <Botao variant={platform === 'linux' ? 'primary' : 'outline-secondary'} onClick={() => onChange('linux')}>
            Linux / macOS
        </Botao>
        <Botao variant={platform === 'windows' ? 'primary' : 'outline-secondary'} onClick={() => onChange('windows')}>
            Windows
        </Botao>
    </HBox>
);

type Props = {
    show: boolean;
    device: NetworkLanDeviceDTO | null;
    /** agent_id do adapter da appliance deste dispositivo — null quando a
     *  appliance não tem agente vinculado. */
    agentId: string | null;
    onClose: () => void;
};

/**
 * Instalador do agente de host para um dispositivo já cadastrado — o mesmo
 * comando estático de AgenteModal (aba "Agente de Host"), aqui pré-apontado
 * pro token da appliance que o dispositivo pertence. Diferença de contexto:
 * a máquina normalmente já tem linha nesta tela (cadastrada pelo MAC antes do
 * agente existir), então o report do host-agent a adota em vez de criar um
 * novo registro aguardando adoção (RepoApplianceLanDispositivo.upsertByMachineUid).
 */
const InstalarAgenteHostModal = ({ show, device, agentId, onClose }: Props) => {
    // O host-agent reporta o SO da máquina — se ela já nos contou que é
    // Windows (ou a tela de cadastro registrou), comece pela aba certa.
    const [platform, setPlatform] = useState<Platform>(() =>
        device?.os_platform?.toLowerCase().includes('windows') ? 'windows' : 'linux');

    const query = useQuery({
        queryKey: ['agente-detalhe', agentId],
        enabled: show && !!agentId,
        queryFn: async () => {
            const res = await axios.get<{ ok: boolean; result: AgenteDetalheDTO }>(`/api/agente/${agentId}`, { withCredentials: true });
            return res.data.result;
        },
    });

    const token = query.data?.host_agent_token ?? null;
    const installBase = getInstallBaseUrl();
    const comando = platform === 'linux'
        ? `curl -fsSL ${installBase}/install-hostagent.sh | sudo sh -s -- --token=${token || '<token>'}`
        : `$env:WEBIP_TOKEN="${token || '<token>'}"; irm ${installBase}/install-hostagent.ps1 | iex`;

    return (
        <Modal
            show={show}
            title={device ? `Instalar agente de host: ${device.nome}` : 'Instalar agente de host'}
            onClose={onClose}
            hideControls
            size="md"
        >
            <VBox style={{ gap: '16px', minWidth: '460px' }}>
                {!agentId && (
                    <AlertBox variant="warning">
                        A appliance deste dispositivo não tem agente (adapter) vinculado — instale o agente do
                        roteador primeiro, na tela de <strong>Agentes</strong>.
                    </AlertBox>
                )}

                {agentId && query.isLoading && (
                    <HBox style={{ justifyContent: 'center', padding: '24px' }}>
                        <Icon path={mdiLoading} spin size={1.5} />
                    </HBox>
                )}

                {agentId && query.isError && (
                    <AlertBox variant="danger">Erro ao carregar o agente da appliance.</AlertBox>
                )}

                {agentId && query.data && !token && (
                    <AlertBox variant="warning">
                        O token de host-agent ainda não existe para o agente <strong>{agentId}</strong> — ele é
                        gerado quando o agente é vinculado a uma appliance. Adote o agente primeiro, na tela de Agentes.
                    </AlertBox>
                )}

                {agentId && token && (
                    <>
                        <AlertBox variant="info">
                            <p>O <strong>agente de host</strong> roda na própria estação (não no roteador) e reporta
                                métricas e patches de SO — é opcional, instale só nas máquinas que quiser monitorar.</p>
                        </AlertBox>

                        <VBox style={{ gap: '6px' }}>
                            <span style={{ fontWeight: 600, fontSize: '14px' }}>
                                Cole este comando no terminal {platform === 'windows' ? '(PowerShell, como administrador)' : '(com sudo)'} da máquina:
                            </span>
                            <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                                O instalador baixa o binário, confere o sha256 e registra o serviço. O script é
                                público e igual pra todo mundo — o que identifica esta instalação é o token do comando.
                            </span>
                            <PlatformToggle platform={platform} onChange={setPlatform} />
                            <CodeBlock value={comando} />
                        </VBox>

                        <AlertBox variant="info">
                            Após rodar o comando, feche este modal e acompanhe nesta tela: em ~1 minuto o status
                            da coluna "Agente" desta linha fica online, e Telemetria e Patches começam a preencher.
                        </AlertBox>
                    </>
                )}
            </VBox>
        </Modal>
    );
};

export default InstalarAgenteHostModal;